<template>
   <dashboard-layout>
      <v-container fluid class="down-top-padding py-0">
         <BaseBreadcrumb :title="page.title" :breadcrumbs="breadcrumbs"></BaseBreadcrumb>

         <v-row dense>
            <v-col cols="12">
               <v-card>
                  <v-toolbar dark color="primary">
                     <v-icon left>mdi-school-outline</v-icon>
                     <v-toolbar-title>Student to Graduate Upgrade</v-toolbar-title>
                  </v-toolbar>

                  <v-card-text>
                     <v-alert type="info" dense text>
                        Complete employment details, upload your Degree completion certificate, then select a new proposer and seconder.
                        Proposer and seconder must be different active Corporate or Fellow members.
                     </v-alert>

                     <v-alert v-if="adminNotes" :type="status === 4 ? 'warning' : 'error'" dense text>
                        <strong>Admin notes:</strong> {{ adminNotes }}
                     </v-alert>

                     <v-card outlined class="mb-5 applicant-card">
                        <v-card-text>
                           <v-row align="center">
                              <v-col cols="12" sm="auto" class="text-center">
                                 <v-avatar size="88" color="blue lighten-5">
                                    <v-img :src="profilePhotoSrc" :alt="memberinfo.fullname"></v-img>
                                 </v-avatar>
                              </v-col>
                              <v-col cols="12" sm="5">
                                 <div class="overline blue--text text--darken-2">Applicant Details</div>
                                 <div class="text-h6 font-weight-bold">{{ memberinfo.fullname }}</div>
                                 <div class="body-2 grey--text text--darken-1">{{ memberinfo.contactemail }}</div>
                                 <div class="body-2 mt-1">MIP Member ID: <strong>{{ memberinfo.memberid || 'Not assigned' }}</strong></div>
                              </v-col>
                              <v-col cols="12" sm="3">
                                 <div class="caption grey--text">Current Member Type</div>
                                 <v-chip color="indigo" dark small class="mt-1">{{ currentMemberTypeLabel }}</v-chip>
                              </v-col>
                              <v-col cols="12" sm="2">
                                 <div class="caption grey--text">Application Status</div>
                                 <v-chip :color="statusColor" dark small class="mt-1">{{ statusLabel }}</v-chip>
                              </v-col>
                           </v-row>
                        </v-card-text>
                     </v-card>

                     <div v-if="currentCertificate" class="mb-4">
                        <strong>Submitted certificate:</strong>
                        <a :href="currentCertificate" target="_blank" class="ml-2">View certificate</a>
                     </div>

                     <v-row dense class="mb-4">
                        <v-col cols="12" md="6">
                           <v-card outlined>
                              <v-card-text class="py-3">
                                 <div class="caption grey--text">Proposer Approval</div>
                                 <v-chip small dark :color="endorsementColor(memberinfo.applgrad_proposer_status)">
                                    {{ endorsementLabel(memberinfo.applgrad_proposer_status) }}
                                 </v-chip>
                                 <div v-if="memberinfo.applgrad_proposer_responded_at" class="caption mt-1">
                                    Responded: {{ formatDate(memberinfo.applgrad_proposer_responded_at) }}
                                 </div>
                              </v-card-text>
                           </v-card>
                        </v-col>
                        <v-col cols="12" md="6">
                           <v-card outlined>
                              <v-card-text class="py-3">
                                 <div class="caption grey--text">Seconder Approval</div>
                                 <v-chip small dark :color="endorsementColor(memberinfo.applgrad_seconder_status)">
                                    {{ endorsementLabel(memberinfo.applgrad_seconder_status) }}
                                 </v-chip>
                                 <div v-if="memberinfo.applgrad_seconder_responded_at" class="caption mt-1">
                                    Responded: {{ formatDate(memberinfo.applgrad_seconder_responded_at) }}
                                 </div>
                              </v-card-text>
                           </v-card>
                        </v-col>
                     </v-row>

                     <table-emp-exp v-if="canSubmit" ref="empexp" class="mb-4" @EmpExpStatus="onEmpExpStatus"></table-emp-exp>
                     <table-emp-cat v-if="canSubmit" ref="empcat" class="mb-3" @EmpCatMounted="onEmpCatMounted"></table-emp-cat>
                     <div v-if="canSubmit" class="text-right mb-5">
                        <v-btn small color="primary" :loading="savingEmpCat" @click="updateEmpCat">
                           Save Employment Category
                        </v-btn>
                     </div>
                     <v-alert v-if="canSubmit && !hasEmploymentExperience" type="warning" dense text>
                        Please add your employment information before requesting endorsement.
                     </v-alert>

                     <v-form v-if="canSubmit" ref="form" v-model="valid" lazy-validation>
                        <div class="section-heading">
                           <v-avatar color="primary" size="30" class="mr-2"><span class="white--text">1</span></v-avatar>
                           <div><strong>Degree Completion Certificate</strong><div class="caption grey--text">Upload an official certificate or completion letter issued by your university.</div></div>
                        </div>
                        <v-file-input
                           v-model="form.certificate"
                           :label="currentCertificate ? '1. Replace Degree Completion Certificate (optional)' : '1. Degree Completion Certificate *'"
                           accept=".pdf,.jpg,.jpeg,.png"
                           prepend-icon="mdi-certificate-outline"
                           outlined dense show-size
                           :rules="certificateRules"
                           hint="PDF/JPG/PNG, maximum 5MB."
                           persistent-hint
                        ></v-file-input>

                        <v-row dense class="mt-3">
                           <v-col cols="12" md="6">
                              <div class="section-heading mb-2">
                                 <v-avatar color="primary" size="30" class="mr-2"><span class="white--text">2</span></v-avatar>
                                 <div><strong>New Proposer</strong><div class="caption grey--text">Select an active Corporate or Fellow member.</div></div>
                              </div>
                              <v-autocomplete
                                 v-model="form.proposer_userid"
                                 :items="eligibleMembers"
                                 item-value="userid"
                                 :item-text="memberLabel"
                                 label="Search proposer by name or Member ID *"
                                 outlined dense clearable
                                 :rules="[requiredRule]"
                              >
                                 <template v-slot:item="{ item }"><member-option :member="item"></member-option></template>
                                 <template v-slot:selection="{ item }"><member-selection :member="item"></member-selection></template>
                              </v-autocomplete>
                           </v-col>
                           <v-col cols="12" md="6">
                              <div class="section-heading mb-2">
                                 <v-avatar color="primary" size="30" class="mr-2"><span class="white--text">3</span></v-avatar>
                                 <div><strong>New Seconder</strong><div class="caption grey--text">Must be a different active Corporate or Fellow member.</div></div>
                              </div>
                              <v-autocomplete
                                 v-model="form.seconder_userid"
                                 :items="eligibleMembers"
                                 item-value="userid"
                                 :item-text="memberLabel"
                                 label="Search seconder by name or Member ID *"
                                 outlined dense clearable
                                 :rules="[requiredRule, differentMemberRule]"
                              >
                                 <template v-slot:item="{ item }"><member-option :member="item"></member-option></template>
                                 <template v-slot:selection="{ item }"><member-selection :member="item"></member-selection></template>
                              </v-autocomplete>
                           </v-col>
                        </v-row>

                        <v-alert v-if="eligibleMembers.length < 2" type="error" dense text>
                           At least two eligible active Corporate/Fellow members are required.
                        </v-alert>
                     </v-form>

                     <v-alert v-else-if="status === 5 && !endorsementsReady" type="info" dense text>
                        Your Graduate Upgrade endorsement request has been sent. Waiting for proposer and seconder approval.
                     </v-alert>
                     <v-alert v-else-if="status === 5 && endorsementsReady && !hasGraduateUpgradePayment" type="warning" dense text>
                        Your proposer and seconder have approved. Please complete the Graduate Upgrade payment or upload a payment receipt before submitting the application.
                        <div class="mt-2"><v-btn small color="primary" dark href="/member/payments?purpose=graduate-upgrade">Go to Payment</v-btn></div>
                     </v-alert>
                     <v-alert v-else-if="status === 5 && endorsementsReady && hasGraduateUpgradePendingPayment" type="info" dense text>
                        Your Graduate Upgrade payment receipt is pending review. You may submit the application for Secretariat review.
                     </v-alert>
                     <v-alert v-else-if="status === 5 && endorsementsReady && hasGraduateUpgradePaid" type="success" dense text>
                        Proposer and seconder have approved, and payment has been recorded. You may submit the application for Secretariat review.
                     </v-alert>
                     <v-alert v-else-if="status === 1" type="info" dense text>
                        Your Graduate Upgrade application has been submitted for Secretariat review.
                     </v-alert>
                     <v-alert v-else-if="status === 2" type="success" dense text>
                        Approved by Secretariat. Your membership type has been changed to Graduate.
                     </v-alert>
                     <v-alert v-else type="error" dense text>
                        This application has been rejected. Contact the Secretariat for clarification.
                     </v-alert>
                  </v-card-text>

                  <v-card-actions>
                     <v-spacer></v-spacer>
                     <v-btn text href="/member/info">Cancel</v-btn>
                     <v-btn
                        v-if="canSubmit"
                        color="primary"
                        :loading="submitting"
                        :disabled="submitting || eligibleMembers.length < 2 || sameMember || !hasEmploymentExperience"
                        @click="submit"
                     >{{ status === 4 ? 'Submit Corrections' : 'Request Endorsement' }}</v-btn>
                     <v-btn
                        v-if="canFinalSubmit"
                        color="success"
                        :loading="submitting"
                        :disabled="submitting"
                        @click="finalSubmit"
                     >Submit Application</v-btn>
                  </v-card-actions>
               </v-card>
            </v-col>
         </v-row>
      </v-container>
   </dashboard-layout>
</template>

<script>
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import BaseBreadcrumb from '@/components/BaseBreadcrumb.vue'
import TableEmpExp from './Tables/TableEmpExp.vue'
import TableEmpCat from './Tables/TableEmpCat.vue'

const MemberOption = {
   props: ['member'],
   template: `<v-list-item class="px-0 candidate-option">
      <v-list-item-avatar size="46"><v-img :src="member.photo_url" :alt="member.fullname"></v-img></v-list-item-avatar>
      <v-list-item-content>
         <v-list-item-title class="font-weight-medium">{{ member.fullname }}</v-list-item-title>
         <v-list-item-subtitle>MIP ID: {{ member.memberid || 'Not assigned' }} · {{ member.membertype === 'COR' ? 'Corporate' : 'Fellow' }}</v-list-item-subtitle>
         <v-list-item-subtitle v-if="member.contactemail">{{ member.contactemail }}</v-list-item-subtitle>
      </v-list-item-content>
      <v-list-item-action><v-chip x-small :color="member.membertype === 'COR' ? 'blue' : 'purple'" dark>{{ member.membertype }}</v-chip></v-list-item-action>
   </v-list-item>`,
}

const MemberSelection = {
   props: ['member'],
   template: `<div class="d-flex align-center selected-member">
      <v-avatar size="28" class="mr-2"><v-img :src="member.photo_url" :alt="member.fullname"></v-img></v-avatar>
      <span class="text-truncate"><strong>{{ member.fullname }}</strong> — {{ member.memberid || 'No ID' }} ({{ member.membertype }})</span>
   </div>`,
}

export default {
   name: 'MOMGraduateUpgrade',
   components: { DashboardLayout, BaseBreadcrumb, MemberOption, MemberSelection, TableEmpExp, TableEmpCat },
   props: {
      memberinfo: { type: Object, required: true },
      eligibleMembers: { type: Array, default: () => [] },
      graduateUpgradePayment: { type: Object, default: null },
   },
   data() {
      return {
         page: { title: 'Graduate Upgrade' },
         breadcrumbs: [
            { text: 'Dashboard', disabled: false, to: '/dashboard' },
            { text: 'Graduate Upgrade', disabled: true },
         ],
         valid: false,
         submitting: false,
         savingEmpCat: false,
         empExpStatus: { hasEmployment: false, totalExperience: 0 },
         form: {
            certificate: null,
            proposer_userid: this.memberinfo.applgrad_proposer_userid || null,
            seconder_userid: this.memberinfo.applgrad_seconder_userid || null,
         },
         requiredRule: value => !!value || 'This field is required.',
      }
   },
   computed: {
      status() { return parseInt(this.memberinfo.applgradstatus || 0) },
      canSubmit() { return [0, 4].includes(this.status) },
      canFinalSubmit() { return this.status === 5 && this.endorsementsReady && this.hasGraduateUpgradePayment },
      adminNotes() { return this.memberinfo.applgrad_notes || '' },
      hasEmploymentExperience() { return this.empExpStatus.hasEmployment },
      endorsementsReady() {
         return parseInt(this.memberinfo.applgrad_proposer_status || 0) === 1
            && parseInt(this.memberinfo.applgrad_seconder_status || 0) === 1
      },
      currentMemberTypeLabel() {
         const labels = {
            STU: 'Student (STU)',
            GRA: 'Graduate (GRA)',
            COR: 'Corporate (COR)',
            FEL: 'Fellow (FEL)',
            ASC: 'Associate (ASC)',
         }
         return labels[this.memberinfo.membertype] || (this.memberinfo.membertype || '-')
      },
      sameMember() {
         return !!this.form.proposer_userid && String(this.form.proposer_userid) === String(this.form.seconder_userid)
      },
      differentMemberRule() {
         return () => !this.sameMember || 'Proposer and seconder must be different members.'
      },
      certificateRules() {
         return this.currentCertificate ? [] : [value => !!value || 'Degree completion certificate is required.']
      },
      currentCertificate() {
         if (!this.memberinfo.applgrad_certificate || !this.memberinfo.applgrad_certificatepath) return ''
         return '/' + this.memberinfo.applgrad_certificatepath + '/' + this.memberinfo.applgrad_certificate
      },
      statusLabel() {
         return ({ 0: 'Not Submitted', 1: 'Pending Secretariat Review', 2: 'Approved', 3: 'Rejected', 4: 'Need Resubmission', 5: 'Pending Endorsement / Payment' })[this.status] || 'Unknown'
      },
      statusColor() {
         return ({ 0: 'grey', 1: 'orange darken-2', 2: 'green', 3: 'red', 4: 'deep-purple', 5: 'blue darken-2' })[this.status] || 'grey'
      },
      hasGraduateUpgradePayment() {
         return this.graduateUpgradePayment && ['01', '00', '02'].includes(String(this.graduateUpgradePayment.trxnstatus || ''))
      },
      hasGraduateUpgradePaid() {
         return this.graduateUpgradePayment && String(this.graduateUpgradePayment.trxnstatus || '') === '01'
      },
      hasGraduateUpgradePendingPayment() {
         return this.graduateUpgradePayment && ['00', '02'].includes(String(this.graduateUpgradePayment.trxnstatus || ''))
      },
      currentUser() {
         return (this.$page && (this.$page.props || this.$page).user) || {}
      },
      profilePhotoSrc() {
         const user = this.currentUser
         return user.id ? `/member/photo/${user.id}?v=${Date.now()}` : (user.profile_photo_url || '')
      },
   },
   methods: {
      memberLabel(item) {
         return `${item.fullname} — ${item.memberid || 'No ID'} (${item.membertype})`
      },
      endorsementLabel(value) {
         return ({ 0: 'Pending', 1: 'Approved', 2: 'Rejected' })[parseInt(value || 0)] || 'Pending'
      },
      endorsementColor(value) {
         return ({ 0: 'orange darken-2', 1: 'green', 2: 'red' })[parseInt(value || 0)] || 'orange darken-2'
      },
      formatDate(value) {
         if (!value) return '-'
         return new Date(value).toLocaleString('en-MY', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      },
      onEmpExpStatus(status) {
         this.empExpStatus = status || { hasEmployment: false, totalExperience: 0 }
      },
      onEmpCatMounted() {
         if (this.$refs.empcat) {
            this.$refs.empcat.setuserid(this.memberinfo.userid)
         }
      },
      updateEmpCat() {
         if (!this.$refs.empcat) return
         this.savingEmpCat = true
         this.$refs.empcat.update()
         setTimeout(() => { this.savingEmpCat = false }, 700)
      },
      submit() {
         if (!this.$refs.form.validate() || this.sameMember) return

         const data = new FormData()
         data.append('proposer_userid', this.form.proposer_userid)
         data.append('seconder_userid', this.form.seconder_userid)
         if (this.form.certificate) data.append('certificate', this.form.certificate)

         this.submitting = true
         this.$inertia.post('/member/apply/upgrade-graduate', data, {
            preserveScroll: true,
            onFinish: () => { this.submitting = false },
         })
      },
      finalSubmit() {
         if (!this.canFinalSubmit) return
         this.submitting = true
         this.$inertia.post('/member/apply/upgrade-graduate/submit', { userid: this.memberinfo.userid }, {
            preserveScroll: true,
            onFinish: () => { this.submitting = false },
         })
      },
   },
   mounted() {
      if (this.$refs.empexp) {
         this.$refs.empexp.setuserid(this.memberinfo.userid)
      }
      if (this.$refs.empcat) {
         this.$refs.empcat.setuserid(this.memberinfo.userid)
      }
   },
}
</script>

<style scoped>
.applicant-card { border-left: 4px solid #1976d2 !important; }
.section-heading { display: flex; align-items: center; margin-bottom: 12px; }
.candidate-option { min-width: 520px; }
.selected-member { max-width: 100%; }
@media (max-width: 600px) { .candidate-option { min-width: 280px; } }
</style>
